import type {
  DiscordGuildChannel,
  FeedbackForm,
  FormRepostFrequency,
} from '@playerbase/types';
import { Card, FormField, RadioCardGroup, Toggle, type RadioCardOption } from '@playerbase/ui';

import {
  type BuilderMetadata,
  type UseFormBuilderState,
} from '../../../games/use-form-builder-state.js';
import { ChannelAccessPanel } from './channel-access-panel.js';

interface RoutingCardProps {
  metadata: BuilderMetadata;
  builder: UseFormBuilderState;
  channels: DiscordGuildChannel[];
  channelsLoading: boolean;
  // True when the active game has no Discord connection. The pickers are
  // disabled in that case with copy nudging the user to Connect Discord.
  hasConnection: boolean;
  // The synced form drives the channel-access panel rendered under the
  // pickers. Null while the page is still loading.
  form: FeedbackForm | null;
  guildId: string | null;
}

const REPOST_OPTIONS: ReadonlyArray<RadioCardOption> = [
  { value: 'never', label: 'Never' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

interface ChannelGroup {
  parentName: string;
  channels: DiscordGuildChannel[];
}

// Group channels under their parent category so the dropdown reads with
// section headings (matching the prototype's watch-channel picker) rather
// than the flat "Category / channel" lines we had before. Top-level
// channels (no parent) bucket into a "Top level" heading at the top.
function groupChannels(channels: DiscordGuildChannel[]): ChannelGroup[] {
  const buckets = new Map<string, DiscordGuildChannel[]>();
  const topLevelKey = '__top__';
  for (const c of channels) {
    const key = c.parentName ?? topLevelKey;
    const list = buckets.get(key);
    if (list === undefined) buckets.set(key, [c]);
    else list.push(c);
  }
  const out: ChannelGroup[] = [];
  if (buckets.has(topLevelKey)) {
    out.push({ parentName: 'Top level', channels: buckets.get(topLevelKey)! });
    buckets.delete(topLevelKey);
  }
  for (const [name, list] of [...buckets.entries()].sort(([a], [b]) =>
    a.localeCompare(b),
  )) {
    out.push({ parentName: name, channels: list });
  }
  return out;
}

// The picker is a plain native <select> with optgroup section headings.
// Channel-access panel slots under the response picker; it's hidden when
// both channels are fully accessible to the bot, otherwise it surfaces
// the recovery copy and a Re-check button.
export function RoutingCard({
  metadata,
  builder,
  channels,
  channelsLoading,
  hasConnection,
  form,
  guildId,
}: RoutingCardProps): JSX.Element {
  // Drop all threads (forum posts and ad-hoc reply threads): the bot
  // posts into channels, not into conversations under them.
  const filteredChannels = channels.filter((c) => c.type !== 'thread');
  const channelGroups = groupChannels(filteredChannels);

  return (
    <Card>
      <Card.Header>
        <Card.Title>Routing &amp; options</Card.Title>
        <Card.Description>
          Where the form is posted, where responses land, and how often it reposts.
        </Card.Description>
      </Card.Header>
      <Card.Content className="flex flex-col gap-4">
        {!hasConnection ? (
          <p className="rounded-md border border-border bg-surface px-3 py-2 text-xs text-text-muted">
            Connect Discord in <strong>Discord connection</strong> to choose channels.
          </p>
        ) : null}

        <FormField>
          <FormField.Label
            className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted"
            data-required
          >
            Survey channel
          </FormField.Label>
          <FormField.Control>
            <ChannelSelect
              value={metadata.surveyChannelId}
              onChange={(channelId) => {
                const sameAs = metadata.surveyChannelId === metadata.responseChannelId;
                builder.updateMetadata({
                  surveyChannelId: channelId,
                  responseChannelId: sameAs ? channelId : metadata.responseChannelId,
                });
              }}
              groups={channelGroups}
              disabled={!hasConnection || channelsLoading}
            />
          </FormField.Control>
          <FormField.HelpText>
            The bot posts the feedback button-message here.
          </FormField.HelpText>
        </FormField>

        <div className="flex items-center justify-between">
          <span className="text-sm text-text">Use the survey channel for responses</span>
          <Toggle
            checked={
              metadata.responseChannelId !== null &&
              metadata.responseChannelId === metadata.surveyChannelId
            }
            onCheckedChange={(checked) => {
              builder.updateMetadata({
                responseChannelId: checked ? metadata.surveyChannelId : null,
              });
            }}
            aria-label="Use the survey channel for responses"
          />
        </div>

        <FormField>
          <FormField.Label
            className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted"
            data-required
          >
            Response channel
          </FormField.Label>
          <FormField.Control>
            <ChannelSelect
              value={metadata.responseChannelId}
              onChange={(channelId) =>
                builder.updateMetadata({ responseChannelId: channelId })
              }
              groups={channelGroups}
              disabled={
                !hasConnection ||
                channelsLoading ||
                metadata.responseChannelId === metadata.surveyChannelId
              }
            />
          </FormField.Control>
          <FormField.HelpText>
            Where the bot posts a summary of each submission.
          </FormField.HelpText>
        </FormField>

        {form !== null ? (
          <ChannelAccessPanel form={form} channels={channels} guildId={guildId} />
        ) : null}

        <FormField>
          <FormField.Label className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Repost frequency
          </FormField.Label>
          <FormField.Control>
            <RadioCardGroup
              name="form-repost-frequency"
              value={metadata.repostFrequency}
              onChange={(value) =>
                builder.updateMetadata({
                  repostFrequency: value as FormRepostFrequency,
                })
              }
              options={REPOST_OPTIONS}
              orientation="grid"
              columns={4}
            />
          </FormField.Control>
          <FormField.HelpText>
            How often the bot reposts the form so it stays visible at the bottom of the channel.
          </FormField.HelpText>
        </FormField>

        <div className="flex items-center justify-between">
          <span className="text-sm text-text">Anonymous responses</span>
          <Toggle
            checked={metadata.anonymous}
            onCheckedChange={(checked) =>
              builder.updateMetadata({ anonymous: checked })
            }
            aria-label="Anonymous responses"
          />
        </div>
        <p className="-mt-2 text-xs text-text-muted">
          {metadata.anonymous
            ? "Every submission is anonymous. Players can't opt in to sharing their handle."
            : 'Players choose for each submission whether to share their Discord handle.'}
        </p>
      </Card.Content>
    </Card>
  );
}

interface ChannelSelectProps {
  value: string | null;
  onChange: (channelId: string | null) => void;
  groups: ChannelGroup[];
  disabled: boolean;
}

function ChannelSelect({
  value,
  onChange,
  groups,
  disabled,
}: ChannelSelectProps): JSX.Element {
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      disabled={disabled}
      className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text disabled:opacity-50"
    >
      <option value="">Select a channel</option>
      {groups.map((group) => (
        <optgroup key={group.parentName} label={group.parentName}>
          {group.channels.map((c) => (
            <option key={c.channelId} value={c.channelId}>
              # {c.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
